import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PublicHeader from '../components/PublicHeader';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Zap, TrendingUp, DollarSign, Gift, CheckCircle2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`
});

export default function Pricing() {
  const navigate = useNavigate();
  const [credits, setCredits] = useState(500);
  const [creditPackages, setCreditPackages] = useState([]);

  console.log('Pricing render - creditPackages:', creditPackages, 'isArray:', Array.isArray(creditPackages));

  useEffect(() => {
    loadCreditPackages();
  }, []);

  const loadCreditPackages = async () => {
    try {
      const response = await api.get('/credit-packages');
      console.log('Credit packages response:', response.data);
      const packages = Array.isArray(response.data) ? response.data : [];
      setCreditPackages(packages);
    } catch (error) {
      console.error('Credit packages load error:', error);
      // Fallback
      setCreditPackages([
        { id: '1', credits: 1000, price: 200, popular: false },
        { id: '2', credits: 5000, price: 900, popular: false },
        { id: '3', credits: 10000, price: 1600, popular: true },
        { id: '4', credits: 25000, price: 3500, popular: false }
      ]);
    }
  };

  const calculatePrice = (creditAmount) => {
    // Yeni fiyatlandırma: ortalama 0.16₺/kredi
    return (creditAmount * 0.16).toFixed(2);
  };

  const calculateQuestions = (creditAmount) => {
    return Math.floor(creditAmount / 10);
  };

  const handlePurchase = () => {
    if (credits < 10) {
      alert('Minimum 10 kredi satın alabilirsiniz');
      return;
    }
    navigate(`/checkout?credits=${credits}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <PublicHeader />
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-green-100 text-green-700 border-green-200">
            <Gift className="h-3 w-3 mr-1" />
            Yeni üyelere 100 kredi hediye
          </Badge>
          <h1 className="text-5xl font-bold mb-4">
            <span className="text-[#FF6000]">Aylık Sabit Ücret Yok!</span>
          </h1>
          <p className="text-xl text-slate-600 mb-2">
            Sadece kullandığınız kadar ödeyin
          </p>
          <p className="text-lg text-slate-500">
            Kredi satın alın, istediğiniz zaman kullanın
          </p>
        </div>

        {/* Credit Calculator */}
        <Card className="max-w-2xl mx-auto mb-12 border-2 border-[#FF6000] shadow-xl">
          <CardHeader className="bg-gradient-to-r from-orange-50 to-amber-50">
            <CardTitle className="text-2xl text-center">Kredi Hesaplayıcı</CardTitle>
          </CardHeader>
          <CardContent className="p-8">
            <div className="space-y-6">
              {/* Credit Input */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Kaç kredi istersiniz?
                </label>
                <Input
                  type="number"
                  min="10"
                  step="10"
                  value={credits}
                  onChange={(e) => setCredits(Math.max(10, parseInt(e.target.value) || 0))}
                  className="text-2xl font-bold text-center h-16"
                  placeholder="Kredi miktarı"
                />
              </div>

              {/* Visual Breakdown */}
              <div className="grid grid-cols-3 gap-4 py-6 border-y">
                <div className="text-center">
                  <Zap className="h-8 w-8 mx-auto mb-2 text-[#FF6000]" />
                  <div className="text-3xl font-bold text-[#FF6000]">{credits}</div>
                  <div className="text-sm text-slate-600">Kredi</div>
                </div>
                <div className="text-center">
                  <TrendingUp className="h-8 w-8 mx-auto mb-2 text-blue-600" />
                  <div className="text-3xl font-bold text-blue-600">{calculateQuestions(credits)}</div>
                  <div className="text-sm text-slate-600">Soru</div>
                </div>
                <div className="text-center">
                  <DollarSign className="h-8 w-8 mx-auto mb-2 text-green-600" />
                  <div className="text-3xl font-bold text-green-600">{calculatePrice(credits)} ₺</div>
                  <div className="text-sm text-slate-600">Toplam</div>
                </div>
              </div>

              {/* Pricing Info */}
              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">💡 Birim fiyat:</span>
                  <span className="font-semibold">1 kredi = 0.14-0.20 ₺</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">🎯 Soru maliyeti:</span>
                  <span className="font-semibold">1 soru = 10 kredi = 1.4-2 ₺</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">⏱️ Geçerlilik:</span>
                  <span className="font-semibold">Süresiz</span>
                </div>
              </div>

              {/* Purchase Button */}
              <Button
                onClick={handlePurchase}
                className="w-full h-14 text-lg bg-[#FF6000] hover:bg-orange-600"
              >
                {calculatePrice(credits)} ₺ Öde ve Satın Al
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Popular Packages */}
        {creditPackages.length > 0 && (
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-center mb-8">Popüler Paketler</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {creditPackages.map((pkg) => (
                <Card 
                  key={pkg.id} 
                  className={`relative ${pkg.popular ? 'border-2 border-[#FF6000] shadow-lg' : ''}`}
                >
                  {pkg.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className="bg-[#FF6000] text-white">Popüler</Badge>
                    </div>
                  )}
                  <CardHeader className="text-center">
                    <div className="text-4xl font-bold text-[#FF6000] mb-2">
                      {pkg.credits}
                    </div>
                    <div className="text-sm text-slate-600">Kredi</div>
                  </CardHeader>
                  <CardContent className="text-center space-y-4">
                    <div>
                      <div className="text-3xl font-bold">{pkg.price} ₺</div>
                      <div className="text-sm text-slate-500">
                        {(pkg.price / pkg.credits).toFixed(2)} ₺/kredi
                      </div>
                    </div>
                    <div className="text-sm text-slate-600">
                      ≈ {Math.floor(pkg.credits / 10)} soru cevaplanır
                    </div>
                    <Button
                      onClick={() => navigate(`/checkout?credits=${pkg.credits}`)}
                      className="w-full bg-[#FF6000] hover:bg-orange-600"
                    >
                      Satın Al
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Features */}
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8">Neler Dahil?</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              'Gelişmiş Merkur AI ile otomatik cevaplar',
              'Hibrit & Full-Auto mod seçenekleri',
              'Trendyol & Hepsiburada entegrasyonu',
              'Sınırsız mağaza bağlantısı',
              'Gerçek zamanlı dashboard',
              'Email destek',
              'Kredi süresiz geçerli',
              'İstediğiniz zaman kredi ekleyin'
            ].map((feature, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-slate-700">{feature}</span>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div className="max-w-3xl mx-auto mt-16">
          <h2 className="text-3xl font-bold text-center mb-8">Sık Sorulan Sorular</h2>
          <div className="space-y-4">
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold mb-2">Krediler ne zaman sona eriyor?</h3>
                <p className="text-slate-600">Krediler süresizdir. İstediğiniz zaman kullanabilirsiniz.</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold mb-2">Minimum kredi miktarı var mı?</h3>
                <p className="text-slate-600">Evet, minimum 10 kredi (5 ₺) satın alabilirsiniz.</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold mb-2">Yeni üyelere hediye var mı?</h3>
                <p className="text-slate-600">Evet! Yeni üyelere 100 kredi (10 soru) hediye ediyoruz.</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold mb-2">Ödeme nasıl yapılıyor?</h3>
                <p className="text-slate-600">Kredi talebinizi gönderin, admin onayından sonra fatura kesip ödeme yaparsınız.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
